import { api, API } from "@/lib/api";

/* Account-wide settings sync. Stores register {apply, read}; user changes call pushPref; login calls pullPrefs.
   Conflicts resolve per key by timestamp (newest wins), tracked locally in rh.prefsMeta. */
const META_KEY = "rh.prefsMeta";
const DEVICE_KEY = "rh.deviceId";
const handlers = {};
let meta = {};
try { meta = JSON.parse(localStorage.getItem(META_KEY) || "{}"); } catch { meta = {}; }
const lastSynced = {};
let pending = {}, timer = null, applying = false, active = false;
const subs = new Set();
let status = { at: null, player: null };
const setStatus = (s) => { status = { ...status, ...s }; subs.forEach((fn) => fn()); };
export const subscribeSync = (fn) => { subs.add(fn); return () => subs.delete(fn); };
export const syncStatus = () => status;

const saveMeta = () => localStorage.setItem(META_KEY, JSON.stringify(meta));
const ts = (iso) => (iso ? Date.parse(iso) || 0 : 0);
const newer = (a, b) => ts(a) > ts(b);

export const onPrefs = (key, h) => { handlers[key] = h; };
export const setSyncActive = (on) => { active = on; if (!on) { pending = {}; clearTimeout(timer); } };

export const deviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) { id = (crypto.randomUUID?.() || String(Math.random()).slice(2)); localStorage.setItem(DEVICE_KEY, id); }
  return id;
};
export const deviceLabel = () => {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? "iPhone" : /Android/.test(ua) ? "Android" : /Mac/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "device";
  const br = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  return `${br} on ${os}`;
};

const flush = async () => {
  const batch = pending; pending = {}; timer = null;
  if (!Object.keys(batch).length) return;
  try {
    const { data } = await api.put("/prefs", { settings: batch });
    Object.entries(data.settings || {}).forEach(([k, e]) => { lastSynced[k] = JSON.stringify(e.value); });
    setStatus({ at: new Date().toISOString() });
  } catch { /* offline — pullPrefs re-pushes anything newer than the server next time */ }
};

export const pushPref = (key, value) => {
  if (applying || !handlers[key]) return;
  const json = JSON.stringify(value);
  if (lastSynced[key] === json) return;
  meta[key] = new Date().toISOString(); saveMeta();
  lastSynced[key] = json;
  if (!active) return;
  pending[key] = { value, updated_at: meta[key] };
  clearTimeout(timer); timer = setTimeout(flush, 1200);
};

export const pullPrefs = async () => {
  if (!active) return null;
  const { data } = await api.get("/prefs");
  const server = data.settings || {};
  // "Clear synced settings" on any device: forget local stamps from before the reset so nothing is re-uploaded.
  if (data.reset_at) for (const k of Object.keys(meta)) if (!newer(meta[k], data.reset_at)) { delete meta[k]; delete lastSynced[k]; }
  applying = true;
  const toPush = {};
  try {
    for (const [k, h] of Object.entries(handlers)) {
      const s = server[k];
      if (s && (!meta[k] || newer(s.updated_at, meta[k]))) {
        h.apply(s.value); meta[k] = s.updated_at; lastSynced[k] = JSON.stringify(s.value);
      } else if (meta[k] && (!s || newer(meta[k], s.updated_at))) {
        const v = h.read(); toPush[k] = { value: v, updated_at: meta[k] }; lastSynced[k] = JSON.stringify(v);
      } else if (s) lastSynced[k] = JSON.stringify(s.value);
    }
  } finally { applying = false; saveMeta(); }
  if (Object.keys(toPush).length) api.put("/prefs", { settings: toPush }).catch(() => {});
  setStatus({ at: new Date().toISOString(), player: data.player });
  return data;
};

export const resetPrefs = async () => {
  await api.delete("/prefs");
  meta = {}; saveMeta();
  Object.keys(lastSynced).forEach((k) => delete lastSynced[k]);
  setStatus({ at: new Date().toISOString(), player: null });
};

/* Last-played queue: throttled while playing, immediate on queue change, keepalive on page hide. */
let playerTimer = null, lastPlayerPush = 0, lastPlayerBody = "";
const playerBody = (snap) => JSON.stringify({ track_ids: snap.queue.slice(0, 500).map((t) => t.id), index: Math.max(0, snap.index), time: Math.round(snap.time || 0), device: deviceLabel(), device_id: deviceId() });
export const pushPlayer = (snap, { delay = 2500 } = {}) => {
  if (!active || !snap?.queue?.length || snap.index < 0) return;
  const body = playerBody(snap);
  if (body === lastPlayerBody) return;
  clearTimeout(playerTimer);
  playerTimer = setTimeout(() => {
    lastPlayerBody = body; lastPlayerPush = Date.now();
    api.put("/prefs/player", JSON.parse(body)).catch(() => {});
  }, delay);
};
export const playerPushedAgo = () => Date.now() - lastPlayerPush;
export const pushPlayerNow = (snap) => {
  if (!active || !snap?.queue?.length || snap.index < 0) return;
  clearTimeout(playerTimer);
  const body = playerBody(snap);
  if (body === lastPlayerBody) return;
  lastPlayerBody = body;
  fetch(`${API}/prefs/player`, { method: "PUT", body, keepalive: true, credentials: "include", headers: { "Content-Type": "application/json" } }).catch(() => {});
};
